import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/bi/AppShell";
import { EmptyState } from "@/components/bi/EmptyState";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AccessGroupsPanel } from "@/components/bi/AccessGroups";
import { useAuth } from "@/context/AuthContext";
import { isTenantAdmin, ROLE_OPTIONS, ROLE_LABELS, normalizeRole } from "@/lib/roles";
import { SessionsTimeline } from "@/pages/Settings";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, UsersRound, UserPlus, Trash2, Shield, Loader2, Lock, Monitor, LogOut, Eye, EyeOff } from "lucide-react";

function UserSessionsButton({ user }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setData(null);
    try {
      const { data: d } = await api.get(`/auth/users/${user.id}/sessions`);
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load sessions"); setData({ items: [], user }); }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const revokeOne = async (sid) => {
    try {
      await api.delete(`/auth/users/${user.id}/sessions/${sid}`);
      toast.success("Session revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revokeAll = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/auth/users/${user.id}/rotate-sessions`);
      toast.success(`Signed out ${d.revoked_count} session${d.revoked_count === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const parseUA = (ua) => {
    if (!ua) return "Unknown client";
    let browser = null;
    if (/Firefox/i.test(ua)) browser = "Firefox";
    else if (/Edg/i.test(ua)) browser = "Edge";
    else if (/Chrome/i.test(ua)) browser = "Chrome";
    else if (/Safari/i.test(ua)) browser = "Safari";
    else if (/curl/i.test(ua)) browser = "cURL";
    else if (/python|axios|node-fetch|okhttp/i.test(ua)) browser = "API client";
    let os = "";
    if (/Windows/i.test(ua)) os = "Windows";
    else if (/Mac OS/i.test(ua)) os = "macOS";
    else if (/Linux/i.test(ua)) os = "Linux";
    else if (/Android/i.test(ua)) os = "Android";
    else if (/iPhone|iPad/i.test(ua)) os = "iOS";
    if (!browser) return ua.length > 60 ? ua.slice(0, 60) + "…" : ua;
    return `${browser}${os ? ` · ${os}` : ""}`;
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid={`user-sessions-btn-${user.id}`} size="sm" variant="ghost"
              onClick={() => setOpen(true)}
              className="text-[color:var(--ink-2)]">
        <Monitor className="w-4 h-4" />
      </Button>
      <DialogContent data-testid="user-sessions-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between gap-3">
            <span>Sessions for {user.name || user.email}</span>
            {data?.items?.length > 0 && (
              <Button data-testid="user-sessions-signout-all" size="sm" variant="outline"
                      onClick={revokeAll} disabled={busy}
                      className="border-[color:var(--danger)]/40 text-[color:var(--danger)] hover:bg-[color:rgba(232,93,106,0.08)]">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <LogOut className="w-3.5 h-3.5 mr-1" />}
                Sign out everywhere
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="user-sessions-list">
          {!data && <div className="py-6 text-center text-[color:var(--ink-3)]"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
          {data && data.items && data.items.length === 0 && <div className="py-6 text-center text-[color:var(--ink-3)]">No active sessions.</div>}
          {data && data.items && data.items.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl border border-[color:var(--glass-border)]">
              <div className="w-9 h-9 rounded-lg bg-[color:var(--surface-2)] flex items-center justify-center shrink-0">
                <Monitor className="w-4 h-4 text-[color:var(--ink-2)]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[color:var(--ink)] font-medium">{parseUA(s.user_agent)}</div>
                <div className="text-[11px] text-[color:var(--ink-3)] font-mono mt-0.5">
                  {s.ip || "unknown IP"} · last seen {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "—"}
                </div>
              </div>
              <Button data-testid={`user-sessions-revoke-${s.id}`} size="sm" variant="ghost"
                      onClick={() => revokeOne(s.id)}
                      className="text-[color:var(--danger)] hover:bg-[color:rgba(232,93,106,0.08)]">
                Revoke
              </Button>
            </div>
          ))}
          {data && data.user && <SessionsTimeline userId={data.user.id} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", role: "viewer" });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isAdmin = isTenantAdmin(user?.role);

  const load = async () => {
    try { const { data } = await api.get("/auth/users"); setUsers(data); }
    catch { setUsers([]); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords don't match"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const { confirm: _c, ...payload } = form;  // eslint-disable-line no-unused-vars
      await api.post("/auth/users", payload);
      toast.success(`User ${form.email} created`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", confirm: "", role: "viewer" });
      setShowPw(false); setShowConfirm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create user"); }
    finally { setSaving(false); }
  };

  const updateRole = async (u, role) => {
    try { await api.put(`/auth/users/${u.id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (u) => {
    try { await api.delete(`/auth/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!isAdmin) {
    return <AppShell title="Team"><div className="px-5 md:px-8 py-10"><EmptyState icon={Lock} title="Admins only" description="Only workspace admins can manage users and access." /></div></AppShell>;
  }

  const addBtn = <Button data-testid="team-add-user" onClick={() => setOpen(true)} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><UserPlus className="w-4 h-4 mr-1" />Create user</Button>;

  return (
    <AppShell title="Team" actions={addBtn}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Create user login</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label>Name</Label><Input data-testid="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" /></div>
            <div><Label>Email</Label><Input data-testid="team-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" /></div>
            <div>
              <Label>Temporary password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="team-password"
                       type={showPw ? "text" : "password"}
                       value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       className="bg-[color:var(--surface)] pr-10"
                       placeholder="at least 8 characters"
                       autoComplete="new-password" />
                <button type="button"
                        data-testid="team-password-toggle"
                        onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        className="absolute inset-y-0 right-0 w-9 flex items-center justify-center text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Repeat password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="team-password-confirm"
                       type={showConfirm ? "text" : "password"}
                       value={form.confirm}
                       onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                       className={`bg-[color:var(--surface)] pr-10 ${form.confirm && form.confirm !== form.password ? "border-[color:var(--danger)]" : ""}`}
                       autoComplete="new-password" />
                <button type="button"
                        data-testid="team-password-confirm-toggle"
                        onClick={() => setShowConfirm((v) => !v)}
                        aria-label={showConfirm ? "Hide password" : "Show password"}
                        className="absolute inset-y-0 right-0 w-9 flex items-center justify-center text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.confirm && form.confirm !== form.password && (
                <div data-testid="team-password-mismatch" className="text-[11px] text-[color:var(--danger)] mt-1">Passwords don&apos;t match</div>
              )}
            </div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="team-role" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="team-create-confirm" onClick={create} disabled={saving} className="bg-[color:var(--brand-ocean)] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create user"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="px-5 md:px-8 py-6">
        <Tabs defaultValue="users">
          <TabsList className="bg-[color:var(--surface-2)] mb-4">
            <TabsTrigger value="users" data-testid="team-tab-users"><Users className="w-4 h-4 mr-1.5" />Users</TabsTrigger>
            <TabsTrigger value="groups" data-testid="team-tab-groups"><UsersRound className="w-4 h-4 mr-1.5" />Groups</TabsTrigger>
          </TabsList>
          <TabsContent value="groups"><AccessGroupsPanel users={users || []} /></TabsContent>
          <TabsContent value="users">
        {users === null ? <Loading label="Loading team" /> : users.length === 0 ? (
          <EmptyState icon={Users} title="No users yet" description="Create user logins and control who can access each dashboard." action={addBtn} />
        ) : (
          <div className="glass-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--surface-2)]"><tr>
                <th className="text-left px-4 py-3 font-medium text-[color:var(--ink-2)]">Name</th>
                <th className="text-left px-4 py-3 font-medium text-[color:var(--ink-2)]">Email</th>
                <th className="text-left px-4 py-3 font-medium text-[color:var(--ink-2)]">Role</th>
                <th className="text-right px-4 py-3 font-medium text-[color:var(--ink-2)]">Actions</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid="team-user-row" className="border-t border-[color:var(--glass-border)]">
                    <td className="px-4 py-3 text-[color:var(--ink)]">{u.name}</td>
                    <td className="px-4 py-3 text-[color:var(--ink-2)]">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.id === user.id ? <Badge className="bg-[color:rgba(18,132,174,0.15)] text-[color:var(--brand-ocean)] border-0"><Shield className="w-3 h-3 mr-1" />{ROLE_LABELS[normalizeRole(u.role)]} (you)</Badge> : (
                        <Select value={normalizeRole(u.role)} onValueChange={(v) => updateRole(u, v)}>
                          <SelectTrigger data-testid={`team-role-${u.id}`} className="w-[160px] h-8 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <UserSessionsButton user={u} />
                        {u.id !== user.id && <Button data-testid={`team-delete-${u.id}`} size="sm" variant="ghost" onClick={() => del(u)} className="text-[color:var(--danger)]"><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
